// Ultimate Card Game (UCG) - Procedural AI Deck Archetypes & Synthesis
//
// Generates diverse, synergistic, and safe decks for the bot AI.
// Features:
//   1. Archetype Core (6 cards) + Flex slots (3-7 cards) -> dynamic deck size (10-13 cards).
//   2. 100% active card pool coverage (all 20 Human and 19 Sul cards).
//   3. Adaptive Counter-Decking: reacts to player threat profiles (Air, Swarm, Heavy, Stealth).
//   4. Cross-Faction Hybrid Synthesis: on Hard, Very Hard, Insane, and Invincible, the AI
//      dissolves faction boundaries and drafts freely across all 39 cards to create the
//      deadliest counter combinations. Base race follows engine majority rule (deriveBaseRace).
//   5. Invariant-Safe: validateAndPatchAiDeck() guarantees singleton, T1 curve, Anti-Air, and tier spread.

import { getActiveCards, getCardByName } from './cards.js';
import { UnseededRNG } from '../engine/rng.js';
import { deriveBaseRace } from '../engine/cardSystem.js';
import { GAMBITS, summarizeDeck } from '../engine/ai/gambits.js';
import { AI_SYNERGY_PAIRS } from './aiSynergyMatrix.js';

// Counter-Matrix according to tactical role alignment:
// - Air Dominance    -> Human: Rocketship, A9 Piranha // Sul: Sentinal Lock
// - Infantry Swarm   -> Human: Tesla Walker           // Sul: Pulp, Precision Strike
// - Heavy Mech/Armor -> Human: Panzer, Deployable Cannon // Sul: Preserver Lock
// - Stealth/Cloaked  -> Human: A9 Grizzly             // Sul: Delving Pillar
export const COUNTER_MATRIX = Object.freeze({
  air: {
    Human: ['Rocketship', 'A9 Piranha'],
    Sul: ['Sentinal Lock'],
    support: ['Marine', 'Enforcer Vulture', 'Drone Command', 'Defective Loaf', 'Tesla Walker']
  },
  swarm: {
    Human: ['Tesla Walker'],
    Sul: ['Pulp', 'Precision Strike'],
    support: ['Marine', 'Devil Patrol', 'Panzer', 'Rejected Loaf', 'Deployable Cannon', 'Apostate Loaf', 'Hot Hand']
  },
  heavy: {
    Human: ['Panzer', 'Deployable Cannon', 'Stickman'],
    Sul: ['Preserver Lock'],
    support: ['Marine', 'Tesla Walker', 'Defective Loaf', 'A9 Grizzly', 'Sentinal Lock', 'Enforcer Vulture']
  },
  stealth: {
    Human: ['A9 Grizzly'],
    Sul: ['Delving Pillar'],
    support: ['Marine', 'Monitor Lock', 'Anti Shade', 'Tesla Walker', 'Rejected Loaf', 'Panzer']
  },
  combined: {
    Human: ['Rocketship', 'Panzer', 'A9 Grizzly', 'Stickman'],
    Sul: ['Sentinal Lock', 'Delving Pillar', 'Preserver Lock'],
    support: ['Tesla Walker', 'Deployable Cannon', 'Marine', 'Defective Loaf', 'Precision Strike', 'Enforcer Vulture']
  },
  siege_fortress: {
    Human: ['Rocketship', 'Deployable Cannon', 'A9 Piranha', 'Stickman'],
    Sul: ['Precision Strike', 'Sentinal Lock'],
    support: ['Tesla Walker', 'Panzer', 'Enforcer Vulture', 'Marine', 'Drone Command']
  }
});

export const AI_ARCHETYPES = Object.freeze({
  // ==========================================
  // HUMAN ARCHETYPES (20 active cards covered)
  // ==========================================
  terran_mech: {
    id: 'terran_mech',
    name: 'Iron Vanguard',
    race: 'Human',
    doctrines: ['artillery', 'bastion', 'standard'],
    core: ['Marine', 'Tesla Walker', 'Panzer', 'Stickman', 'A9 Grizzly', 'Hot Hand'],
    flex: ['Rocketship', 'Deployable Cannon', 'Drone Command', 'Shade', 'Wheelie Bomber', 'A9 Piranha', 'Pink Beret', 'Orchestra', 'The Salvage Company', 'Reconsider', 'Hacker']
  },

  terran_air: {
    id: 'terran_air',
    name: 'Sky Superiority',
    race: 'Human',
    doctrines: ['airWing', 'rush', 'standard'],
    core: ['Marine', 'Devil Patrol', 'Rocketship', 'A9 Piranha', 'Drone Command', 'Invasion Capsule Sling'],
    flex: ['Wheelie Bomber', 'Fodder', 'Mental Extraction', 'Shade', 'Tesla Walker', 'Panzer', 'Hot Hand', 'A9 Pigeon']
  },

  terran_swarm: {
    id: 'terran_swarm',
    name: 'Drop & Swarm',
    race: 'Human',
    doctrines: ['rush', 'standard'],
    core: ['Marine', 'Devil Patrol', 'Hot Hand', 'Fodder', 'Wheelie Bomber', 'Invasion Capsule Sling'],
    flex: ['Tesla Walker', 'Rocketship', 'A9 Grizzly', 'Mental Extraction', 'Panzer', 'Orchestra', 'Stickman', 'Pink Beret', 'Reconsider']
  },

  terran_covert_siege: {
    id: 'terran_covert_siege',
    name: 'Covert & Heavy Artillery',
    race: 'Human',
    doctrines: ['bastion', 'artillery', 'standard'],
    core: ['Marine', 'Shade', 'Stickman', 'Orchestra', 'A9 Grizzly', 'Deployable Cannon'],
    flex: ['Tesla Walker', 'Panzer', 'A9 Piranha', 'Fodder', 'Mental Extraction', 'Reconsider', 'The Salvage Company', 'Hot Hand', 'Hacker']
  },

  terran_high_tech: {
    id: 'terran_high_tech',
    name: 'High-Tech Hybrid',
    race: 'Human',
    doctrines: ['standard', 'rush', 'artillery'],
    core: ['Marine', 'Tesla Walker', 'A9 Grizzly', 'Hot Hand', 'Rocketship', 'Stickman'],
    flex: ['Shade', 'Deployable Cannon', 'Drone Command', 'Wheelie Bomber', 'Devil Patrol', 'Orchestra', 'Mental Extraction', 'Reconsider', 'Hacker', 'Congress Module']
  },

  // ==========================================
  // SUL ARCHETYPES (19 active cards covered)
  // ==========================================
  sul_swarm: {
    id: 'sul_swarm',
    name: 'Swarm Overdrive',
    race: 'Sul',
    doctrines: ['rush', 'standard'],
    core: ['Defective Loaf', 'Rejected Loaf', 'Pulp', 'Precision Strike', 'Forced Overstimulation', 'Enforcer Vulture'],
    flex: ['Parasite Vulture', 'Delving Pillar', 'Sentinal Lock', 'Apostate Loaf', 'Processing Pillar', 'Monitor Lock', 'Forgotten Loaf', 'Claiming Pillar', 'Ambush']
  },

  sul_bioplasma: {
    id: 'sul_bioplasma',
    name: 'Bio-Plasma Ramp',
    race: 'Sul',
    doctrines: ['bastion', 'standard'],
    core: ['Defective Loaf', 'Delving Pillar', 'Harvesting Pillar', 'Processing Pillar', 'Sentinal Lock', 'Looting Vulture'],
    flex: ['Rejected Loaf', 'Preserver Lock', 'Bastion Lock', 'Parasite Vulture', 'Claiming Pillar', 'Anti Shade', 'Precision Strike', 'Retreat Protocol', 'Pulp']
  },

  sul_airborne: {
    id: 'sul_airborne',
    name: 'Toxic Sky Infestation',
    race: 'Sul',
    doctrines: ['airWing', 'rush', 'standard'],
    core: ['Rejected Loaf', 'Precision Strike', 'Sentinal Lock', 'Enforcer Vulture', 'Parasite Vulture', 'Looting Vulture'],
    flex: ['Defective Loaf', 'Monitor Lock', 'Apostate Loaf', 'Forced Overstimulation', 'Delving Pillar', 'Experimental Metamorphosis', 'Pulp']
  },

  sul_hive_defense: {
    id: 'sul_hive_defense',
    name: 'Hive Bastion & Metamorphosis',
    race: 'Sul',
    doctrines: ['bastion', 'artillery', 'standard'],
    core: ['Apostate Loaf', 'Rejected Loaf', 'Preserver Lock', 'Monitor Lock', 'Anti Shade', 'Experimental Metamorphosis'],
    flex: ['Harvesting Pillar', 'Claiming Pillar', 'Bastion Lock', 'Sentinal Lock', 'Precision Strike', 'Enforcer Vulture', 'Processing Pillar', 'Forgotten Loaf', 'Retreat Protocol', 'Pulp']
  },

  sul_mutagenic: {
    id: 'sul_mutagenic',
    name: 'Mutagenic Forge & Artillery',
    race: 'Sul',
    doctrines: ['artillery', 'standard'],
    core: ['Defective Loaf', 'Apostate Loaf', 'Processing Pillar', 'Sentinal Lock', 'Preserver Lock', 'Looting Vulture'],
    flex: ['Rejected Loaf', 'Delving Pillar', 'Monitor Lock', 'Parasite Vulture', 'Anti Shade', 'Forced Overstimulation', 'Pulp']
  }
});

// How strong each archetype is as a deck, measured on 22.10.2026 with
// tools/ai-eval/counterMatrix.mjs: the archetype's own deck (Hard) against the
// four reference player decks that contain Human cards (Normal pilots), mean
// win rate (docs/AI_MASTERPLAN_2026-10.md, "Stand nach B20"). The pure Sul
// archetypes lose 97-100% against Human-heavy decks, the strongest Human ones
// win 80-84%: deck strength, not what it counters, is what the matrix
// measures (bot deck 50% of the variance, deck x player interaction 1%).
export const CHASSIS_STRENGTH = Object.freeze({
  terran_high_tech: 0.84,
  terran_mech: 0.81,
  terran_covert_siege: 0.80,
  terran_air: 0.70,
  terran_swarm: 0.49,
  sul_airborne: 0.13,
  sul_swarm: 0.03,
  sul_bioplasma: 0.02,
  sul_hive_defense: 0.02,
  sul_mutagenic: 0.01
});

// Picks an archetype. `bias` 0 is the old uniform pick (and consumes the rng
// exactly as it always did); at 1 an archetype's weight is its measured
// strength to the 4th power - the three strongest Human decks come up about
// 80% of the time, the other two Human ones the rest, and the pure-Sul decks
// (strength <= 0.13) practically never. That is the price of the strength:
// less variety (bias 0.5 keeps more of it and measured nearly as strong).
export function pickArchetype(archetypes, bias, rng, exclude = null) {
  let pool = archetypes;
  if (exclude && archetypes.length > 1) {
    const excludeList = Array.isArray(exclude) ? exclude : [exclude];
    const filtered = archetypes.filter(a => !excludeList.some(ex => a.id === ex || (typeof ex === 'string' && ex.startsWith(a.id))));
    if (filtered.length > 0) pool = filtered;
  }
  if (!bias || bias <= 0 || pool.length <= 1) return rng.pick(pool);
  const weights = pool.map(a => Math.pow(Math.max(0.02, CHASSIS_STRENGTH[a.id] ?? 0.3), 4 * bias));
  let roll = rng.next() * weights.reduce((x, y) => x + y, 0);
  for (let i = 0; i < pool.length; i++) {
    roll -= weights[i];
    if (roll < 0) return pool[i];
  }
  return pool[pool.length - 1];
}

// The threat class the counter-drafting builds against. A large (40-card)
// player deck has a deck profile (js/engine/ai/deckProfile.js) and could be
// answered with a package matched to what stands out in it - but the matrix of
// tools/ai-eval/counterMatrix.mjs shows no meaningful deck x deck counter
// structure, and in vsplayer the matched packages LOSE to the generic one:
// 67% generic, 64% matched; with strong chassis 76% against 69%
// (docs/AI_MASTERPLAN_2026-10.md, "Stand nach B20"). Large decks are answered
// with the generic package; the profile stays available as information.
export function threatToDraftAgainst(opponentProfile) {
  if (!opponentProfile) return undefined;
  return opponentProfile.deckProfile ? 'combined' : opponentProfile.primaryThreat;
}

export function getArchetypesForRace(race) {
  const archetypes = Object.values(AI_ARCHETYPES).filter(a => a.race === race).map(a => ({
    ...a,
    core: [...a.core],
    flex: [...a.flex]
  }));

  // Universal Auto-Coverage Guarantee:
  // Any newly added active card for this race that is not in any archetype's core or flex
  // is dynamically attached to the flex pool of the race's archetypes so the AI can draft and play it.
  const covered = new Set();
  for (const a of archetypes) {
    for (const name of a.core) covered.add(name);
    for (const name of a.flex) covered.add(name);
  }
  for (const card of getActiveCards()) {
    if ((!race || card.race === race) && !covered.has(card.name)) {
      covered.add(card.name);
      if (archetypes.length > 0) {
        archetypes[0].flex.push(card.name);
      }
    }
  }
  return archetypes;
}

// Verifies that every active card for a given race is represented in at least one archetype
export function getAllCoveredCards(race) {
  const covered = new Set();
  for (const a of getArchetypesForRace(race)) {
    for (const name of a.core) covered.add(name);
    for (const name of a.flex) covered.add(name);
  }
  // Future-proof fallback: any active card for this race is guaranteed to be covered in the universal pool
  for (const card of getActiveCards()) {
    if (!race || card.race === race) {
      covered.add(card.name);
    }
  }
  return covered;
}

/**
 * Returns dynamic counter matrix augmented with any active cards from getActiveCards()
 */
export function getDynamicCounterMatrix() {
  const matrix = {
    air: { Human: [...COUNTER_MATRIX.air.Human], Sul: [...COUNTER_MATRIX.air.Sul], support: [...COUNTER_MATRIX.air.support] },
    swarm: { Human: [...COUNTER_MATRIX.swarm.Human], Sul: [...COUNTER_MATRIX.swarm.Sul], support: [...COUNTER_MATRIX.swarm.support] },
    heavy: { Human: [...COUNTER_MATRIX.heavy.Human], Sul: [...COUNTER_MATRIX.heavy.Sul], support: [...COUNTER_MATRIX.heavy.support] },
    stealth: { Human: [...COUNTER_MATRIX.stealth.Human], Sul: [...COUNTER_MATRIX.stealth.Sul], support: [...COUNTER_MATRIX.stealth.support] },
    combined: { Human: [...COUNTER_MATRIX.combined.Human], Sul: [...COUNTER_MATRIX.combined.Sul], support: [...COUNTER_MATRIX.combined.support] },
    siege_fortress: { Human: [...COUNTER_MATRIX.siege_fortress.Human], Sul: [...COUNTER_MATRIX.siege_fortress.Sul], support: [...COUNTER_MATRIX.siege_fortress.support] }
  };

  for (const card of getActiveCards()) {
    const race = card.race;
    if (race !== 'Human' && race !== 'Sul') continue;
    const name = card.name;

    // Air counters (targetsAir with damage or tether)
    if (card.targetsAir !== false && ((card.damage || 0) > 0 || card.hasDualTether || card.hasBeamTether)) {
      if (!matrix.air[race].includes(name)) matrix.air[race].push(name);
    }
    // Swarm counters (Area splash or anti-infantry)
    if ((card.splashRadius || 0) > 0 || (card.capsuleSplashRadius || 0) > 0 || card.antiMarineBonus) {
      if (!matrix.swarm[race].includes(name)) matrix.swarm[race].push(name);
    }
    // Heavy counters
    if (card.deflectionScaling || card.projectileType === 'Energy' || (card.damage || 0) >= 60) {
      if (!matrix.heavy[race].includes(name)) matrix.heavy[race].push(name);
    }
    // Stealth detectors
    if (card.isDetector || card.detectsCloak || card.unitType === 'DelvingPillar') {
      if (!matrix.stealth[race].includes(name)) matrix.stealth[race].push(name);
    }
  }

  return matrix;
}

// Helper: maximum copies allowed per card in a competitive AI deck (strictly capping at max 1/3 of deck)
function getMaxCopiesForCard(card, deckSize = 18) {
  if (!card) return 3;
  const oneThirdCap = Math.max(1, Math.floor(deckSize / 3));
  // Base modules can only be built ONCE per match; Claiming Pillar is unique territory anchor (max 1 copy); Ambush is high-impact frontline surprise (max 1 copy)
  if (card.type === 'Module' || card.isModule || card.name === 'Claiming Pillar' || card.name === 'Ambush' || card.spellEffect === 'ambush') return 1;
  // Anti Shade: strict role constraint (cannot attack buildings, cost 47, max 1 copy per deck)
  if (card.name === 'Anti Shade' || (card.cannotAttackBuildings && (card.cost || 0) >= 40)) return 1;
  // Static structures and kamikaze tech units cap at 2 copies
  if (card.type === 'Structure' || card.isStructure || card.isKamikaze) return Math.min(2, oneThirdCap);
  // Ultra-heavy Tier-3 units (cost >= 33) cap at 2 copies to prevent opening hand bricks
  if ((card.cost || 0) >= 33) return Math.min(2, oneThirdCap);
  return Math.min(3, oneThirdCap);
}

// Helper: identifies a true Tier-1 frontline combat unit (excludes spells, modules, kamikazes, and 12-cost detectors)
function isTrueFrontlineT1(card) {
  if (!card || card.isSpell || card.type !== 'Unit' || card.isStructure || card.isModule || card.isKamikaze) {
    return false;
  }
  const cost = card.cost || 10;
  return (card.tier === 1 || cost <= 9) && cost <= 10 && ((card.damage || 0) >= 5 || Boolean(card.reflectDamageRatio));
}

// Validates and repairs an AI deck to strictly uphold the safety invariants:
// - Deck size in the 15-20 range (Option A).
// - No card exceeds its role copy cap (max 3; Modules max 1; Structures/Kamikazes/Ultra-Heavies max 2).
// - At least 4 real Tier-1 frontline combat units (cost <= 10, non-spell, non-kamikaze).
// - At least 2 reliable Anti-Air cards.
// - Full Tier spread (T1, T2, T3).
export function validateAndPatchAiDeck(cardNames, race = null) {
  const activePool = race ? getActiveCards().filter(c => c.race === race) : getActiveCards();
  if (activePool.length === 0) return [];
  const activeMap = new Map(activePool.map(c => [c.name, c]));
  const getObj = (name) => activeMap.get(name) || getCardByName(name);

  // 1. Filter to active cards & enforce role-aware copy caps (strictly capping at max 1/3 of deck)
  const deck = [];
  const counts = new Map();
  const deckCap = Math.max(15, cardNames.length);
  for (const name of cardNames) {
    if (activeMap.has(name)) {
      const card = getObj(name);
      const maxCopies = getMaxCopiesForCard(card, deckCap);
      const current = counts.get(name) || 0;
      if (current < maxCopies) {
        counts.set(name, current + 1);
        deck.push(name);
      }
    }
  }

  const isCheapOrT1 = (c) => c && ((c.cost || 10) <= 12 || c.tier === 1);
  const isAntiAir = (c) => c && c.targetsAir !== false && ((c.damage || 0) > 0 || !!c.hasDualTether || !!c.hasBeamTether);

  // 2. Guarantee Anti-Air protection (at least 2 copies)
  let aaCount = deck.filter(name => isAntiAir(getObj(name))).length;
  if (aaCount < 2) {
    const fallbackAA = (race === 'Sul')
      ? (activeMap.has('Sentinal Lock') ? 'Sentinal Lock' : (activeMap.has('Monitor Lock') ? 'Monitor Lock' : 'Enforcer Vulture'))
      : 'Marine';
    while (aaCount < 2 && (counts.get(fallbackAA) || 0) < 3) {
      deck.push(fallbackAA);
      counts.set(fallbackAA, (counts.get(fallbackAA) || 0) + 1);
      aaCount++;
    }
  }

  // 3. Guarantee at least 4 real Tier-1 frontline combat units (so AI never starts without early fighters)
  let frontlineCount = deck.filter(name => isTrueFrontlineT1(getObj(name))).length;
  if (frontlineCount < 4) {
    const frontlineCandidates = activePool.filter(c => isTrueFrontlineT1(c));
    for (const cand of frontlineCandidates) {
      while (frontlineCount < 4 && (counts.get(cand.name) || 0) < 3) {
        deck.push(cand.name);
        counts.set(cand.name, (counts.get(cand.name) || 0) + 1);
        frontlineCount++;
      }
      if (frontlineCount >= 4) break;
    }
  }

  // Fallback cheap check in case custom pools have no standard frontline units
  let cheapCount = deck.filter(name => isCheapOrT1(getObj(name))).length;
  if (cheapCount < 4) {
    const cheapCandidates = activePool.filter(c => isCheapOrT1(c));
    for (const cand of cheapCandidates) {
      while (cheapCount < 4 && (counts.get(cand.name) || 0) < getMaxCopiesForCard(cand)) {
        deck.push(cand.name);
        counts.set(cand.name, (counts.get(cand.name) || 0) + 1);
        cheapCount++;
      }
      if (cheapCount >= 4) break;
    }
  }

  // 4. Guarantee all 3 Tiers (T1, T2, T3) are represented
  for (let tier = 1; tier <= 3; tier++) {
    const hasTier = deck.some(name => getObj(name)?.tier === tier);
    if (!hasTier) {
      const tierCand = activePool.find(c => c.tier === tier && c.name !== 'The Salvage Company' && c.name !== 'Reconsider')
        || activePool.find(c => c.tier === tier);
      if (tierCand && (counts.get(tierCand.name) || 0) < getMaxCopiesForCard(tierCand)) {
        deck.push(tierCand.name);
        counts.set(tierCand.name, (counts.get(tierCand.name) || 0) + 1);
      }
    }
  }

  // 5. Guarantee minimum size of 15 cards (preferring fusible T1/T2 combat units)
  while (deck.length < 15) {
    const underCapped = deck.find(n => {
      const c = getObj(n);
      return (counts.get(n) || 0) < getMaxCopiesForCard(c) && (c?.cost || 10) <= 22 && c?.type === 'Unit' && !c?.isKamikaze;
    });
    if (underCapped) {
      deck.push(underCapped);
      counts.set(underCapped, (counts.get(underCapped) || 0) + 1);
    } else {
      const unused = activePool.find(c => (counts.get(c.name) || 0) < getMaxCopiesForCard(c) && (c.cost || 10) <= 25);
      if (!unused) break;
      deck.push(unused.name);
      counts.set(unused.name, (counts.get(unused.name) || 0) + 1);
    }
  }

  // 6. Guarantee maximum size of 20 cards (preserve frontline T1 and at least 1 copy of core/counter cards)
  while (deck.length > 20) {
    let removed = false;
    // First trim excess copies of expensive cards (cost >= 18) that have > 1 copy
    for (let i = deck.length - 1; i >= 0; i--) {
      const name = deck[i];
      const c = getObj(name);
      if ((counts.get(name) || 0) > 1 && (c?.cost || 10) >= 18) {
        deck.splice(i, 1);
        counts.set(name, counts.get(name) - 1);
        removed = true;
        break;
      }
    }
    // Next trim any card with > 2 copies (as long as frontlineCount stays >= 4)
    if (!removed) {
      for (let i = deck.length - 1; i >= 0; i--) {
        const name = deck[i];
        const c = getObj(name);
        if ((counts.get(name) || 0) > 2 && (!isTrueFrontlineT1(c) || frontlineCount > 4)) {
          deck.splice(i, 1);
          counts.set(name, counts.get(name) - 1);
          if (isTrueFrontlineT1(c)) frontlineCount--;
          removed = true;
          break;
        }
      }
    }
    // Fallback: trim any duplicate card
    if (!removed) {
      for (let i = deck.length - 1; i >= 0; i--) {
        const name = deck[i];
        if ((counts.get(name) || 0) > 1) {
          deck.splice(i, 1);
          counts.set(name, counts.get(name) - 1);
          removed = true;
          break;
        }
      }
    }
    if (!removed) break;
  }

  // 7. Hard invariant: Anti Shade and Claiming Pillar must NEVER exceed 1/3 of the deck
  const oneThirdCap = Math.max(1, Math.floor(deck.length / 3));
  for (const restricted of ['Anti Shade', 'Claiming Pillar']) {
    while ((counts.get(restricted) || 0) > oneThirdCap) {
      const idx = deck.lastIndexOf(restricted);
      if (idx !== -1) {
        deck.splice(idx, 1);
        counts.set(restricted, counts.get(restricted) - 1);
      } else break;
    }
  }

  return deck;
}

/**
 * Expands unique card names into a focused, synergistic, role-balanced multi-copy deck (15-20 cards).
 * - Preserves Archetype Core identity (`coreCards`) and mandatory counters (`boostCards`).
 * - Uses seeded RNG jitter so AI decks vary organically from match to match.
 * - Enforces balanced mana curve:
 *   * 2 distinct T1 frontline fighters (5-6 copies total)
 *   * 3-4 midrange / counter / Pulp wildcard units (2-3 copies each)
 *   * 1-2 heavy finishers / tech spells / structures (1-2 copies each)
 */
export function expandDeckToMultiCopy(
  uniqueCardNames,
  targetSize = 17,
  { boostCards = [], coreCards = [], flexCards = [], rng = UnseededRNG, race = null, injectPulp = false } = {}
) {
  const activePool = race ? getActiveCards().filter(c => c.race === race) : getActiveCards();
  const cardMap = new Map(activePool.map(c => [c.name, c]));
  const boostSet = new Set(boostCards);
  const coreSet = new Set(coreCards);

  // Dead-weight non-combat cards to heavily deprioritize in combat decks
  const deadWeight = new Set(['The Salvage Company', 'Reconsider']);

  const candidatePool = [...uniqueCardNames];
  if (injectPulp && !candidatePool.includes('Pulp') && (!race || race === 'Sul')) {
    candidatePool.push('Pulp');
  }

  // Ensure candidatePool has at least 2 real T1 frontline combat units available to choose from
  const availableT1 = candidatePool.filter(name => isTrueFrontlineT1(cardMap.get(name) || getCardByName(name)));
  if (availableT1.length < 2) {
    const fallbackT1Pool = activePool.filter(c => isTrueFrontlineT1(c));
    for (const fc of fallbackT1Pool) {
      if (!candidatePool.includes(fc.name)) {
        candidatePool.push(fc.name);
        if (candidatePool.filter(n => isTrueFrontlineT1(cardMap.get(n) || getCardByName(n))).length >= 2) break;
      }
    }
  }

  // Score candidate cards dynamically based on archetype core, counter boost, role, and seeded RNG variety
  const validCandidates = [];
  const seen = new Set();

  for (const name of candidatePool) {
    if (!name || seen.has(name)) continue;
    seen.add(name);
    const card = cardMap.get(name) || getCardByName(name);
    if (!card) continue;

    let priority = 40;
    if (boostSet.has(name)) priority += 120;
    if (coreSet.has(name)) priority += 70;
    if (injectPulp && name === 'Pulp') priority += 65;

    // Seeded RNG jitter (0..28) so non-mandatory slots vary across matches!
    const jitter = (rng && typeof rng.next === 'function') ? (rng.next() * 28) : 0;
    priority += jitter;

    const cost = card.cost || 10;
    const isKamikaze = Boolean(card.isKamikaze);
    const isModule = card.type === 'Module' || Boolean(card.isModule);
    const isStructure = card.type === 'Structure' || Boolean(card.isStructure);

    if (isTrueFrontlineT1(card)) {
      priority += 35; // True frontline early fighter
    } else if (isKamikaze) {
      priority += 5; // Tech burst only — never mistake kamikaze burst for continuous DPS!
    } else if (isModule) {
      priority -= 15; // Support module only (max 1 copy)
    } else if (!card.isSpell && !isStructure) {
      // Combat unit role bonuses
      if (card.targetsAir !== false && ((card.damage || 0) > 0 || card.hasDualTether || card.hasBeamTether)) {
        priority += 20;
      }
      if (card.isDetector || card.detectsCloak) {
        priority += 20;
      }
      if ((card.splashRadius || 0) > 0 || (card.capsuleSplashRadius || 0) > 0) {
        priority += 18;
      }
      const dps = (card.damage || 0) / Math.max(0.4, card.attackSpeed || 1);
      if ((card.damage || 0) >= 40 || dps >= 18) {
        priority += 20;
      }
      if ((card.hp || 0) >= 140 || (card.deflectionRating || 0) >= 0.20 || card.reflectDamageRatio) {
        priority += 20;
      }
    }

    // Heavy penalty for non-combat utility buildings or hand cyclers in combat decks
    if (deadWeight.has(name) || (isStructure && card.canAttack === false && card.name !== 'Claiming Pillar')) {
      priority -= 90;
    }

    validCandidates.push({ name, card, priority });
  }

  // Sort candidates by priority descending
  validCandidates.sort((a, b) => b.priority - a.priority);

  // Role-Bucketed Selection (7-8 distinct cards):
  // Guarantee:
  // - All boosted counter cards (`boostSet`)
  // - At least 2 distinct real T1 frontline combat units
  // - Remaining slots filled by highest-priority core/midrange/finisher cards
  const desiredDistinct = Math.min(10, Math.max(8, Math.floor(targetSize / 1.9)));
  const selectedMap = new Map();

  // 1. Always include mandatory boostCards first
  for (const entry of validCandidates) {
    if (boostSet.has(entry.name)) {
      selectedMap.set(entry.name, entry);
    }
  }

  // 2. Guarantee at least 2 distinct real T1 frontline combat units
  let t1Selected = Array.from(selectedMap.values()).filter(e => isTrueFrontlineT1(e.card)).length;
  for (const entry of validCandidates) {
    if (t1Selected >= 2) break;
    if (!selectedMap.has(entry.name) && isTrueFrontlineT1(entry.card)) {
      selectedMap.set(entry.name, entry);
      t1Selected++;
    }
  }

  // 2b. Guarantee at least 1-2 distinct signature flex cards from the archetype (if available)
  if (Array.isArray(flexCards) && flexCards.length > 0) {
    const availableFlex = flexCards.filter(name => candidatePool.includes(name) && !selectedMap.has(name) && !deadWeight.has(name));
    if (availableFlex.length > 0) {
      const flexGoal = targetSize >= 17 ? 2 : 1;
      let flexAdded = 0;
      const shuffledFlex = [...availableFlex].sort(() => ((rng && typeof rng.next === 'function' ? rng.next() : Math.random()) - 0.5));
      for (const fname of shuffledFlex) {
        if (flexAdded >= flexGoal) break;
        const entry = validCandidates.find(e => e.name === fname);
        if (entry) {
          selectedMap.set(fname, entry);
          flexAdded++;
        }
      }
    }
  }

  // 3. Fill remaining distinct slots from sorted candidates (capping ultra-heavy >=30 cost cards at max 2 distinct)
  let heavyDistinct = Array.from(selectedMap.values()).filter(e => (e.card.cost || 0) >= 30).length;
  for (const entry of validCandidates) {
    if (selectedMap.size >= desiredDistinct) break;
    if (selectedMap.has(entry.name)) continue;
    const isHeavy = (entry.card.cost || 0) >= 30;
    if (isHeavy && heavyDistinct >= 2) continue;
    selectedMap.set(entry.name, entry);
    if (isHeavy) heavyDistinct++;
  }

  const chosenNames = Array.from(selectedMap.keys());

  // Allocate copies based on tactical curve (preventing 3x 33-cost / 40-cost hand bricks):
  const counts = new Map();
  let t1FrontlineAssigned = 0;

  for (let i = 0; i < chosenNames.length; i++) {
    const name = chosenNames[i];
    const card = cardMap.get(name) || getCardByName(name);
    const cost = card?.cost || 10;
    const maxCap = getMaxCopiesForCard(card, targetSize);

    if (maxCap === 1) {
      counts.set(name, 1);
    } else if (isTrueFrontlineT1(card)) {
      // First T1 frontline unit gets 3 copies, second gets 2-3 copies
      const copies = t1FrontlineAssigned === 0 ? 3 : 2;
      counts.set(name, Math.min(maxCap, copies));
      t1FrontlineAssigned++;
    } else if (cost >= 28 || card?.type === 'Structure' || card?.isStructure || card?.isSpell || card?.isKamikaze || card?.name === 'Anti Shade' || card?.cannotAttackBuildings) {
      // Heavy finishers, structures, spells, kamikazes, and Anti Shade start at 1 copy (even if in boostSet)
      counts.set(name, 1);
    } else if (name === 'Pulp') {
      counts.set(name, Math.min(maxCap, 2));
    } else if ((boostSet.has(name) || coreSet.has(name)) && cost <= 20) {
      counts.set(name, Math.min(maxCap, 2));
    } else {
      counts.set(name, Math.min(maxCap, 2));
    }
  }

  // Adjust total copies to match targetSize exactly
  let currentTotal = Array.from(counts.values()).reduce((a, b) => a + b, 0);

  // If under targetSize: upgrade T1 frontline and midrange fusible units (cost <= 20) up to 3 copies first!
  while (currentTotal < targetSize) {
    let increased = false;
    for (const name of chosenNames) {
      const card = cardMap.get(name) || getCardByName(name);
      const maxCap = getMaxCopiesForCard(card, targetSize);
      const c = counts.get(name) || 0;
      if (c < maxCap && (card?.cost || 10) <= 20 && !card?.isSpell && !card?.isStructure && !card?.isKamikaze) {
        counts.set(name, c + 1);
        currentTotal++;
        increased = true;
        if (currentTotal >= targetSize) break;
      }
    }
    if (!increased) {
      for (const name of chosenNames) {
        const card = cardMap.get(name) || getCardByName(name);
        const maxCap = getMaxCopiesForCard(card, targetSize);
        const c = counts.get(name) || 0;
        if (c < maxCap) {
          counts.set(name, c + 1);
          currentTotal++;
          increased = true;
          if (currentTotal >= targetSize) break;
        }
      }
    }
    if (!increased) break;
  }

  // If over targetSize: decrease non-frontline cards with > 2 copies, then non-boost cards with > 1 copy
  while (currentTotal > targetSize) {
    let decreased = false;
    for (let i = chosenNames.length - 1; i >= 0; i--) {
      const name = chosenNames[i];
      const card = cardMap.get(name) || getCardByName(name);
      if (counts.get(name) > 2 && !isTrueFrontlineT1(card)) {
        counts.set(name, counts.get(name) - 1);
        currentTotal--;
        decreased = true;
        break;
      }
    }
    if (!decreased) {
      for (let i = chosenNames.length - 1; i >= 0; i--) {
        const name = chosenNames[i];
        const card = cardMap.get(name) || getCardByName(name);
        if (counts.get(name) > 1 && !boostSet.has(name) && !isTrueFrontlineT1(card)) {
          counts.set(name, counts.get(name) - 1);
          currentTotal--;
          decreased = true;
          break;
        }
      }
    }
    if (!decreased) {
      for (let i = chosenNames.length - 1; i >= 0; i--) {
        const name = chosenNames[i];
        if (counts.get(name) > 1) {
          counts.set(name, counts.get(name) - 1);
          currentTotal--;
          decreased = true;
          break;
        }
      }
    }
    if (!decreased) break;
  }

  const result = [];
  for (const [name, count] of counts.entries()) {
    for (let i = 0; i < count; i++) {
      result.push(name);
    }
  }
  return result;
}

export const WIN_STREAK_FLEX_POOL = Object.freeze({
  Human: ['The Salvage Company', 'Wheelie Bomber', 'Fodder', 'Drone Command', 'Mental Extraction', 'Reconsider'],
  Sul: ['Rejected Loaf', 'Apostate Loaf', 'Harvesting Pillar', 'Claiming Pillar', 'Experimental Metamorphosis', 'Forced Overstimulation', 'Ambush'],
  Universal: [
    'The Salvage Company', 'Wheelie Bomber', 'Fodder', 'Drone Command', 'Mental Extraction', 'Reconsider',
    'Rejected Loaf', 'Apostate Loaf', 'Harvesting Pillar', 'Claiming Pillar', 'Experimental Metamorphosis', 'Forced Overstimulation', 'Ambush'
  ]
});

/**
 * Applies Win-Streak Relief (Flex Slots) to an AI deck if the AI has won 3+ consecutive matches in this session.
 * Replaces 2-3 non-core tail cards with alternative/experimental/off-meta cards from WIN_STREAK_FLEX_POOL,
 * preserving at least 7 core archetype cards while creating new tactical dynamics and openings for the player.
 */
export function applyWinStreakFlexSlots(deckResult, opponentProfile, rng = UnseededRNG, targetRace = null) {
  if (!deckResult || !Array.isArray(deckResult.cards)) return deckResult;
  if (!opponentProfile?.hasWinStreakRelief && (opponentProfile?.consecutiveWins || 0) < 3) {
    return deckResult;
  }

  const currentCards = [...deckResult.cards];
  if (currentCards.length < 8) return deckResult;

  const flexSlotCount = (rng.next() < 0.5) ? 2 : 3;
  const isHybrid = Boolean(deckResult.isHybrid);
  const race = targetRace || deckResult.race || (isHybrid ? null : 'Human');

  const pool = isHybrid
    ? WIN_STREAK_FLEX_POOL.Universal
    : (WIN_STREAK_FLEX_POOL[race] || WIN_STREAK_FLEX_POOL.Universal);

  const candidateFlexCards = pool.filter(c => !currentCards.includes(c));

  // Dynamic Auto-Expansion Fallback:
  // If new cards are added to the game or the curated pool has fewer unused candidates than needed,
  // dynamically supplement from the active card pool, strictly excluding hard-counters against the player.
  if (candidateFlexCards.length < flexSlotCount) {
    const threat = threatToDraftAgainst(opponentProfile);
    const hardCounters = new Set(
      threat && COUNTER_MATRIX[threat]
        ? [
            ...(COUNTER_MATRIX[threat].Human || []),
            ...(COUNTER_MATRIX[threat].Sul || [])
          ]
        : []
    );
    const activeCandidates = getActiveCards()
      .filter(c => (isHybrid || c.race === race) && !currentCards.includes(c.name) && !hardCounters.has(c.name))
      .map(c => c.name);

    for (const card of activeCandidates) {
      if (!candidateFlexCards.includes(card)) {
        candidateFlexCards.push(card);
      }
    }
  }

  if (candidateFlexCards.length === 0) return deckResult;

  // Shuffle candidates
  const shuffledCandidates = [...candidateFlexCards];
  for (let i = shuffledCandidates.length - 1; i > 0; i--) {
    const j = Math.floor(rng.next() * (i + 1));
    [shuffledCandidates[i], shuffledCandidates[j]] = [shuffledCandidates[j], shuffledCandidates[i]];
  }

  // Preserve at least 7 core cards, replacing tail slots
  const coreCutoff = Math.max(7, currentCards.length - flexSlotCount);
  const preservedCore = currentCards.slice(0, coreCutoff);

  const newCards = [...preservedCore];
  const introducedFlex = [];

  for (let i = 0; i < flexSlotCount && i < shuffledCandidates.length; i++) {
    newCards.push(shuffledCandidates[i]);
    introducedFlex.push(shuffledCandidates[i]);
  }

  // Re-patch to guarantee all invariants (T1 curve, anti-air, singleton, min 10 cards)
  const patchedCards = validateAndPatchAiDeck(newCards, isHybrid ? null : race);
  const derivedRace = isHybrid ? deriveBaseRace(patchedCards) : (deckResult.race || race);

  return {
    ...deckResult,
    race: derivedRace,
    hasWinStreakRelief: true,
    consecutiveWins: opponentProfile?.consecutiveWins || 3,
    flexSlotsCount: introducedFlex.length,
    flexCardsIntroduced: introducedFlex,
    cards: patchedCards
  };
}

// Procedural AI Deck Generator
// Synthesizes a thematic, randomized, invariant-safe deck for a match.
// If opponentProfile is provided and active:
//   - On Hard, Very Hard, Insane, Invincible: drafts cross-faction hybrid counter decks from all 39 cards.
//   - On Medium: drafts pure faction counter decks.
//   - On Easy: no adaptation.
function buildProceduralAiDeck({
  race = 'Human',
  doctrineId = null,
  difficulty = 'hard',
  rng = UnseededRNG,
  requestedArchetype = null,
  chassisBias = 0,
  targetSize = null,
  opponentProfile = null
}) {
  const raceCards = race ? getActiveCards().filter(c => c.race === race) : getActiveCards();
  if (race && raceCards.length === 0) {
    return { race, isHybrid: false, cards: [] };
  }

  // 1. Determine target deck size based on difficulty (Option A: 15-20 cards)
  let finalSize = targetSize;
  if (!finalSize) {
    switch (difficulty) {
      case 'invincible':
      case 'insane':
        finalSize = rng.pick([15, 16]);
        break;
      case 'veryHard':
        finalSize = rng.pick([15, 16, 17]);
        break;
      case 'hard':
      default:
        finalSize = rng.pick([16, 17, 18]);
        break;
      case 'medium':
        finalSize = rng.pick([17, 18, 19]);
        break;
      case 'easy':
        finalSize = rng.pick([18, 19, 20]);
        break;
    }
  }
  finalSize = Math.max(15, Math.min(20, finalSize));

  // 1b. Champion Deck Mimicry Path (triggered after 4+ consecutive losses against player)
  if (opponentProfile?.isChampionMimicry && Array.isArray(opponentProfile?.championDeck) && opponentProfile.championDeck.length >= 8 && difficulty !== 'easy') {
    const champCards = opponentProfile.championDeck.map(c => typeof c === 'string' ? c : c?.name).filter(Boolean);
    const mimicPool = [];
    const addCard = (name) => {
      if (name && !mimicPool.includes(name)) mimicPool.push(name);
    };

    // Keep the player's core setup (7-8 cards)
    const coreCount = Math.min(8, Math.max(6, champCards.length - 2));
    for (let i = 0; i < coreCount && i < champCards.length; i++) {
      addCard(champCards[i]);
    }

    // Tech in dedicated high-impact counter tools
    if (opponentProfile.hasHacker) {
      addCard('Hacker');
    }
    if (opponentProfile.hasStealth) {
      addCard('A9 Grizzly');
    }
    if (opponentProfile.hasSiegeFortress || opponentProfile.hasArtillery) {
      addCard('Precision Strike');
    }
    addCard('A9 Piranha');
    addCard('Rocketship');

    const boostList = ['A9 Piranha', 'Rocketship', 'Precision Strike'];
    if (opponentProfile.hasHacker) boostList.push('Hacker');

    const multiCopyCards = expandDeckToMultiCopy(mimicPool, finalSize, {
      boostCards: boostList,
      coreCards: champCards.slice(0, coreCount),
      rng,
      injectPulp: true,
      race: null
    });
    const safeCards = validateAndPatchAiDeck(multiCopyCards, null);
    const derivedRace = deriveBaseRace(safeCards);

    return applyWinStreakFlexSlots({
      race: derivedRace,
      isHybrid: true,
      counterThreat: 'champion_mimicry',
      archetypeId: 'champion_mimicry',
      archetypeName: 'Champion Mimicry (Reflected Meta)',
      optimizationFactor: 1.0,
      cards: safeCards
    }, opponentProfile, rng, null);
  }

  // 2. Adaptive Counter-Decking Path (if player profile is available)
  const threat = threatToDraftAgainst(opponentProfile);
  const isAdaptive = threat && threat !== 'balanced' && difficulty !== 'easy';
  const excludeArchetypes = opponentProfile?.recentBotArchetypes || (opponentProfile?.lastBotArchetype ? [opponentProfile.lastBotArchetype] : null);

  if (isAdaptive && COUNTER_MATRIX[threat]) {
    const matrixEntry = COUNTER_MATRIX[threat];
    const isCrossFaction = Boolean(opponentProfile.isCrossFactionAllowed);
    const optimizationFactor = opponentProfile.optimizationFactor ?? 1.0;

    if (isCrossFaction) {
      if (optimizationFactor < 0.8) {
        // 50% PARTIAL ADAPTIVE COUNTER-DRAFTING (Hard after Match 1)
        // Keeps the rolled race archetype core, but injects ~50% of the counter package
        // from both factions (hybrid partial counter-play).
        const raceArchetypes = getArchetypesForRace(race);
        const chosenArchetype = pickArchetype(raceArchetypes.length > 0 ? raceArchetypes : getArchetypesForRace('Human'), chassisBias, rng, excludeArchetypes);

        const partialCards = [];
        const addCard = (name) => {
          if (name && !partialCards.includes(name)) partialCards.push(name);
        };

        // Pick ~50% of hard counters from both factions
        const humanCounters = matrixEntry.Human || [];
        const sulCounters = matrixEntry.Sul || [];

        const halfHumanCount = Math.max(1, Math.round(humanCounters.length * 0.5));
        const halfSulCount = Math.max(1, Math.round(sulCounters.length * 0.5));

        for (let i = 0; i < halfHumanCount && i < humanCounters.length; i++) {
          addCard(humanCounters[i]);
        }
        for (let i = 0; i < halfSulCount && i < sulCounters.length; i++) {
          addCard(sulCounters[i]);
        }

        // Safety Net: If opponent has Hacker, guarantee Hacker counter (for Human/hybrid)
        if (opponentProfile.hasHacker) {
          const hackerCounter = (race === 'Sul' && !isCrossFaction) ? 'Precision Strike' : 'Hacker';
          addCard(hackerCounter);
        }

        // Safety Net: If opponent has Stealth, guarantee a detector
        if (opponentProfile.hasStealth) {
          const detector = (race === 'Sul' && !isCrossFaction) ? 'Delving Pillar' : 'A9 Grizzly';
          addCard(detector);
        }

        // Safety Net: If opponent has Artillery (Orchestra), guarantee mobile armor/push
        if (opponentProfile.hasArtillery) {
          const antiArtillery = (race === 'Sul' && !isCrossFaction) ? 'Anti Shade' : 'Panzer';
          addCard(antiArtillery);
        }

        // Safety Net: If opponent has Siege Fortress (Artillery + Frontline Structures),
        // guarantee bunker buster (Precision Strike) and air bypass (A9 Piranha)
        if (opponentProfile.hasSiegeFortress || (opponentProfile.hasArtillery && (opponentProfile.structureCount || 0) >= 1)) {
          addCard('Precision Strike');
          addCard('A9 Piranha');
        }

        // Add 1-2 situational support cards
        const supportShuffled = [...(matrixEntry.support || [])];
        for (let i = supportShuffled.length - 1; i > 0; i--) {
          const j = Math.floor(rng.next() * (i + 1));
          [supportShuffled[i], supportShuffled[j]] = [supportShuffled[j], supportShuffled[i]];
        }
        for (let i = 0; i < 2 && i < supportShuffled.length; i++) {
          addCard(supportShuffled[i]);
        }

        // Fill remaining slots with the chosen archetype's core and flex
        for (const card of chosenArchetype.core) {
          addCard(card);
        }
        for (const card of chosenArchetype.flex) {
          addCard(card);
        }

        const hardCounters = [
          ...humanCounters.slice(0, halfHumanCount),
          ...sulCounters.slice(0, halfSulCount)
        ];
        if (opponentProfile.hasHacker) hardCounters.push((race === 'Sul' && !isCrossFaction) ? 'Precision Strike' : 'Hacker');
        if (opponentProfile.hasStealth) hardCounters.push((race === 'Sul' && !isCrossFaction) ? 'Delving Pillar' : 'A9 Grizzly');
        if (opponentProfile.hasArtillery) hardCounters.push((race === 'Sul' && !isCrossFaction) ? 'Anti Shade' : 'Panzer');
        if (opponentProfile.hasSiegeFortress || (opponentProfile.hasArtillery && (opponentProfile.structureCount || 0) >= 1)) {
          hardCounters.push('Precision Strike');
        }
        const multiCopyCards = expandDeckToMultiCopy(partialCards, finalSize, {
          boostCards: hardCounters.slice(0, 4),
          coreCards: chosenArchetype.core,
          flexCards: chosenArchetype.flex,
          rng,
          injectPulp: true,
          race: null
        });
        const safeCards = validateAndPatchAiDeck(multiCopyCards, null);
        const derivedRace = deriveBaseRace(safeCards);

        return applyWinStreakFlexSlots({
          race: derivedRace,
          isHybrid: true,
          counterThreat: threat,
          archetypeId: `${chosenArchetype.id}_anti_${threat}_50`,
          subArchetypeId: chosenArchetype.id,
          archetypeName: `${chosenArchetype.name} (Anti-${threat.toUpperCase()} 50%)`,
          optimizationFactor: 0.5,
          cards: safeCards
        }, opponentProfile, rng, null);
      }

      // FULL CROSS-FACTION HYBRID DRAFTING (100% maximal optimization)
      // Rotates across all 10 thematic archetypes as the tactical chassis while
      // combining counter-picks from BOTH Human and Sul + Pulp wildcard synergy!
      const allArchetypes = Object.values(AI_ARCHETYPES);
      const matchingChassis = doctrineId
        ? allArchetypes.filter(a => a.doctrines.includes(doctrineId))
        : allArchetypes;
      const chosenChassis = pickArchetype(matchingChassis.length > 0 ? matchingChassis : allArchetypes, chassisBias, rng, excludeArchetypes);

      const hybridCards = [];
      const addCards = (list) => {
        for (const name of list) {
          if (!hybridCards.includes(name)) hybridCards.push(name);
        }
      };

      // Add direct hard counters from both factions
      addCards(matrixEntry.Human || []);
      addCards(matrixEntry.Sul || []);

      // Safety Net: If opponent has Hacker, guarantee Hacker counter
      if (opponentProfile.hasHacker) {
        addCards(['Hacker']);
      }

      // Safety Net: If opponent has Stealth, guarantee a detector
      if (opponentProfile.hasStealth) {
        addCards(['A9 Grizzly', 'Delving Pillar']);
      }

      // Safety Net: If opponent has Artillery (Orchestra), guarantee mobile armor/push
      if (opponentProfile.hasArtillery) {
        addCards(['Panzer', 'A9 Piranha']);
      }

      // Safety Net: If opponent has Siege Fortress (Artillery + Frontline Structures),
      // guarantee bunker buster (Precision Strike) and air mobility flankers
      if (opponentProfile.hasSiegeFortress || (opponentProfile.hasArtillery && (opponentProfile.structureCount || 0) >= 1)) {
        addCards(['Precision Strike', 'Rocketship', 'A9 Piranha']);
      }

      // Inject the rolled archetype chassis core (gives each rematch a fresh tactical identity & early curve)
      addCards(chosenChassis.core || []);

      // Add situational synergistic support
      const supportShuffled = [...(matrixEntry.support || []), ...(chosenChassis.flex || [])];
      for (let i = supportShuffled.length - 1; i > 0; i--) {
        const j = Math.floor(rng.next() * (i + 1));
        [supportShuffled[i], supportShuffled[j]] = [supportShuffled[j], supportShuffled[i]];
      }
      for (const card of supportShuffled) {
        if (!hybridCards.includes(card)) hybridCards.push(card);
      }

      // Build a focused mandatory boost list (max 4-5 distinct counter cards) so the deck
      // isn't diluted across 11 singletons and retains 3-copy density + Pulp wildcard + chassis identity!
      let focusedCounters = [];
      if (threat === 'combined') {
        const detectorPick = (chosenChassis.race === 'Sul' && rng.next() < 0.6) ? 'Delving Pillar' : 'A9 Grizzly';
        const airPick = rng.pick(['Rocketship', 'A9 Piranha']);
        const heavyPick = rng.pick(['Panzer', 'Tesla Walker', 'Stickman', 'Sentinal Lock']);
        focusedCounters.push(detectorPick, airPick, heavyPick, 'Pulp');
        if (opponentProfile.hasSiegeFortress || (opponentProfile.hasArtillery && (opponentProfile.structureCount || 0) >= 1)) {
          focusedCounters.push('Precision Strike');
        }
        if (opponentProfile.hasHacker && !focusedCounters.includes('Hacker')) {
          focusedCounters.push('Hacker');
        }
      } else {
        focusedCounters = [...(matrixEntry.Human || []), ...(matrixEntry.Sul || [])];
        if (opponentProfile.hasHacker && !focusedCounters.includes('Hacker')) {
          focusedCounters.push('Hacker');
        }
        if (opponentProfile.hasStealth) {
          focusedCounters.push(rng.next() < 0.5 ? 'A9 Grizzly' : 'Delving Pillar');
        }
        if (opponentProfile.hasArtillery) {
          focusedCounters.push(rng.next() < 0.5 ? 'Panzer' : 'A9 Piranha');
        }
        if (opponentProfile.hasSiegeFortress || (opponentProfile.hasArtillery && (opponentProfile.structureCount || 0) >= 1)) {
          focusedCounters.push('Precision Strike');
          if (!focusedCounters.includes('Rocketship') && !focusedCounters.includes('A9 Piranha')) {
            focusedCounters.push('Rocketship');
          }
        }
      }
      const multiCopyCards = expandDeckToMultiCopy(hybridCards, finalSize, {
        boostCards: focusedCounters,
        coreCards: chosenChassis.core,
        flexCards: chosenChassis.flex,
        rng,
        injectPulp: true,
        race: null
      });
      const safeCards = validateAndPatchAiDeck(multiCopyCards, null);
      const derivedRace = deriveBaseRace(safeCards);

      return applyWinStreakFlexSlots({
        race: derivedRace,
        isHybrid: true,
        counterThreat: threat,
        archetypeId: `hybrid_anti_${threat}`,
        subArchetypeId: chosenChassis.id,
        archetypeName: `Hybrid Anti-${threat.toUpperCase()} (${chosenChassis.name})`,
        optimizationFactor: 1.0,
        cards: safeCards
      }, opponentProfile, rng, null);
    } else {
      // SINGLE-FACTION COUNTER DRAFTING (Medium)
      // Injects faction-specific counter picks into the archetype
      const factionCounters = matrixEntry[race] || [];
      const raceArchetypes = getArchetypesForRace(race);
      const chosenArchetype = pickArchetype(raceArchetypes, chassisBias, rng, excludeArchetypes);

      const deckCards = [...factionCounters];
      if (opponentProfile.hasHacker && race === 'Human') {
        if (!deckCards.includes('Hacker')) deckCards.push('Hacker');
      }
      if (opponentProfile.hasStealth) {
        const detector = race === 'Sul' ? 'Delving Pillar' : 'A9 Grizzly';
        if (!deckCards.includes(detector)) deckCards.push(detector);
      }
      if (opponentProfile.hasArtillery) {
        const antiArtillery = race === 'Sul' ? 'Anti Shade' : 'Panzer';
        if (!deckCards.includes(antiArtillery)) deckCards.push(antiArtillery);
      }
      deckCards.push(...chosenArchetype.core);

      for (const flexCard of chosenArchetype.flex) {
        if (!deckCards.includes(flexCard)) deckCards.push(flexCard);
      }

      const boostList = [...factionCounters];
      if (opponentProfile.hasHacker && race === 'Human' && !boostList.includes('Hacker')) {
        boostList.push('Hacker');
      }

      const multiCopyCards = expandDeckToMultiCopy(deckCards, finalSize, {
        boostCards: boostList,
        coreCards: chosenArchetype.core,
        flexCards: chosenArchetype.flex,
        rng,
        race
      });
      const safeCards = validateAndPatchAiDeck(multiCopyCards, race);

      return applyWinStreakFlexSlots({
        race,
        isHybrid: false,
        counterThreat: threat,
        archetypeId: chosenArchetype.id,
        subArchetypeId: chosenArchetype.id,
        archetypeName: chosenArchetype.name,
        optimizationFactor: 0.5,
        cards: safeCards
      }, opponentProfile, rng, race);
    }
  }

  // 3. Default Procedural Archetype Synthesis (Neutral / Initial / Non-Adaptive)
  const raceArchetypes = getArchetypesForRace(race);
  if (raceArchetypes.length === 0) {
    const allRace = getActiveCards().filter(c => c.race === race).map(c => c.name);
    const multiCopyCards = expandDeckToMultiCopy(allRace, finalSize, { rng, race });
    return applyWinStreakFlexSlots({ race, isHybrid: false, cards: validateAndPatchAiDeck(multiCopyCards, race) }, opponentProfile, rng, race);
  }

  let chosenArchetype = null;
  if (requestedArchetype && AI_ARCHETYPES[requestedArchetype]?.race === race) {
    chosenArchetype = AI_ARCHETYPES[requestedArchetype];
  } else if (doctrineId) {
    const matching = raceArchetypes.filter(a => a.doctrines.includes(doctrineId));
    chosenArchetype = matching.length > 0 ? pickArchetype(matching, chassisBias, rng, excludeArchetypes) : pickArchetype(raceArchetypes, chassisBias, rng, excludeArchetypes);
  } else {
    chosenArchetype = pickArchetype(raceArchetypes, chassisBias, rng, excludeArchetypes);
  }

  const chosenCards = [...chosenArchetype.core];
  const flexPool = [...chosenArchetype.flex];

  for (let i = flexPool.length - 1; i > 0; i--) {
    const j = Math.floor(rng.next() * (i + 1));
    [flexPool[i], flexPool[j]] = [flexPool[j], flexPool[i]];
  }

  for (const flexCard of flexPool) {
    if (!chosenCards.includes(flexCard)) {
      chosenCards.push(flexCard);
    }
  }

  if (chosenCards.length < 8) {
    const allRaceCards = getActiveCards().filter(c => c.race === race).map(c => c.name);
    for (const cardName of allRaceCards) {
      if (!chosenCards.includes(cardName)) {
        chosenCards.push(cardName);
      }
    }
  }

  const multiCopyCards = expandDeckToMultiCopy(chosenCards, finalSize, {
    coreCards: chosenArchetype.core,
    flexCards: chosenArchetype.flex,
    rng,
    injectPulp: (race === 'Sul' && difficulty !== 'easy'),
    race
  });
  const safeCards = validateAndPatchAiDeck(multiCopyCards, race);

  return applyWinStreakFlexSlots({
    race,
    isHybrid: false,
    archetypeId: chosenArchetype.id,
    subArchetypeId: chosenArchetype.id,
    archetypeName: chosenArchetype.name,
    cards: safeCards
  }, opponentProfile, rng, race);
}


// --- deck coupling ---------------------------------------------------------------
//
// A match plan chosen BEFORE the deck is built (js/engine/ai/strategist.js
// preview()) gets a deck that can play it: if the finished deck lacks what the
// plan needs (gambits.js `requires`), copies of the deck's most doubled cards
// make room for the plan's core cards (`core`). A deck that already carries the
// plan is left exactly as it is, so the coupling costs nothing where it is not needed.
// A plan that is not coupled (race has no core for it) is simply not offered.

function dominantRace(names) {
  let human = 0;
  let sul = 0;
  for (const n of names) {
    const r = getCardByName(n)?.race;
    if (r === 'Human') human++;
    else if (r === 'Sul') sul++;
  }
  return sul > human ? 'Sul' : 'Human';
}

export function coupleDeckToGambit(cardNames, gambitId) {
  const gambit = GAMBITS[gambitId];
  const names = [...cardNames];
  if (!gambit) return { cards: names, coupled: false, swaps: 0 };
  const carries = (list) => gambit.requires(summarizeDeck(list));
  if (carries(names)) return { cards: names, coupled: true, swaps: 0 };
  const need = gambit.core?.(dominantRace(names), names);
  if (!need || need.length === 0) return { cards: names, coupled: false, swaps: 0 };

  let swaps = 0;
  for (const add of need) {
    if (carries(names)) break;
    const card = getCardByName(add);
    if (!card) continue;
    const have = names.filter(n => n === add).length;
    if (have >= getMaxCopiesForCard(card)) continue;
    // Room: one copy of the card the deck has most of (never one of the core cards itself).
    const counts = {};
    for (const n of names) counts[n] = (counts[n] || 0) + 1;
    const victim = Object.entries(counts)
      .filter(([n, c]) => c > 1 && !need.includes(n))
      .sort((a, b) => b[1] - a[1])[0];
    if (!victim) {
      // No doubled card to give up: the deck may grow by one while it is below the cap.
      if (names.length < 20) { names.push(add); swaps++; }
      continue;
    }
    names[names.lastIndexOf(victim[0])] = add;
    swaps++;
  }
  const safe = validateAndPatchAiDeck(names, null);
  return { cards: safe, coupled: carries(safe), swaps };
}

export function coupleDeckToSynergies(cardNames, rng = null) {
  const names = [...cardNames];
  const activePairs = AI_SYNERGY_PAIRS.filter(p => !p.cardA.startsWith('MODULE:') && !p.cardA.startsWith('STATE:'));
  let swaps = 0;

  for (const pair of activePairs) {
    if (names.includes(pair.cardA) && !names.includes(pair.cardB)) {
      const cardB = getCardByName(pair.cardB);
      if (!cardB) continue;
      if (rng && rng.next() > 0.6) continue;

      const counts = {};
      for (const n of names) counts[n] = (counts[n] || 0) + 1;
      const victim = Object.entries(counts)
        .filter(([n, c]) => c > 1 && n !== pair.cardA)
        .sort((a, b) => b[1] - a[1])[0];
      if (victim) {
        names[names.lastIndexOf(victim[0])] = pair.cardB;
        swaps++;
      }
    }
  }

  const safe = validateAndPatchAiDeck(names, null);
  return { cards: safe, swaps };
}

// The deck builder, optionally coupled to the plan the bot has picked for the match.
export function generateProceduralAiDeck(options) {
  const deck = buildProceduralAiDeck(options);
  if (options?.gambitId && deck?.cards?.length) {
    const result = coupleDeckToGambit(deck.cards, options.gambitId);
    deck.cards = result.cards;
    deck.gambitCoupled = result.coupled;
    deck.gambitSwaps = result.swaps;
  }
  if (deck?.cards?.length && options?.isHybrid) {
    const synResult = coupleDeckToSynergies(deck.cards, options?.rng || null);
    deck.cards = synResult.cards;
    deck.synergySwaps = synResult.swaps;
  }
  return deck;
}
